import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { CompanyAndTenantInfo } from '../../core/models/company-and-tenant-info';
import { CompanySettingsService } from '../../core/services/company-settings.service';
import { MatDialog } from '@angular/material/dialog';
import { CreateRealmDialogComponent } from '../dialogs/create-realm-dialog/create-realm-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { DeleteRealmDialogComponent } from '../dialogs/delete-realm-dialog/delete-realm-dialog.component';
import { TenantService } from '../../core/services/tenant.service';
import { RemoveRealmDialogComponent } from '../dialogs/remove-realm-dialog/remove-realm-dialog.component';

@Component({
  selector: 'app-realms-overview',
  templateUrl: './realms-overview.component.html',
  styleUrls: ['./realms-overview.component.scss'],
  providers: [CompanySettingsService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class RealmsOverviewComponent implements OnInit {
  displayedColumns: string[] = [
    'tenantId',
    'tenantIdentifier',
    'tenantName',
    'displayName',
    'gdprDeletedAt',
    'deleteRealm',
  ];
  readonly dataSource = signal<CompanyAndTenantInfo[]>([]);
  readonly tenantIdentifierFilter = new FormControl('', { nonNullable: true });
  readonly hideGdprDeleted = new FormControl(false, { nonNullable: true });
  private readonly tenantIdentifierFilterValue = toSignal(
    this.tenantIdentifierFilter.valueChanges,
    { initialValue: this.tenantIdentifierFilter.value },
  );
  private readonly hideGdprDeletedValue = toSignal(
    this.hideGdprDeleted.valueChanges,
    { initialValue: this.hideGdprDeleted.value },
  );
  readonly filteredDataSource = computed(() => {
    const tenantIdentifierFilter = this.tenantIdentifierFilterValue()
      .trim()
      .toLowerCase();
    const hideGdprDeleted = this.hideGdprDeletedValue();

    return this.dataSource().filter((tenant) => {
      if (hideGdprDeleted && tenant.gdprDeletedAt) {
        return false;
      }

      return (
        !tenantIdentifierFilter ||
        tenant.tenantIdentifier
          ?.toLowerCase()
          .includes(tenantIdentifierFilter) === true
      );
    });
  });

  constructor(
    private readonly tenantService: TenantService,
    private readonly companyService: CompanySettingsService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.reloadCompanyAndTenantInfos();
  }

  reloadCompanyAndTenantInfos() {
    this.companyService.getCompanyAndTenantInfos().subscribe((infos) => {
      this.dataSource.set(
        [...infos].sort((a, b) => {
          if (a.gdprDeletedAt == null) return b.gdprDeletedAt == null ? 0 : -1;
          if (b.gdprDeletedAt == null) return 1;
          return (
            new Date(a.gdprDeletedAt).getTime() -
            new Date(b.gdprDeletedAt).getTime()
          );
        }),
      );
    });
  }

  onCreateNewRealmClicked() {
    const dialogRef = this.dialog.open(CreateRealmDialogComponent);
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.reloadCompanyAndTenantInfos();
        this.snackBar.open(
          `Realm ${result} wurde erfolgreich erstellt.`,
          'Ok',
          { duration: 5000 },
        );
      }
    });
  }

  onRowSelected(selected: CompanyAndTenantInfo) {
    if (selected.tenantIdentifier) {
      void this.router.navigate(['/realm', selected.tenantIdentifier]);
    }
  }

  onDeleteRealm(event: Event, element: CompanyAndTenantInfo) {
    event.stopPropagation();
    this.dialog
      .open(DeleteRealmDialogComponent, { data: element })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.reloadCompanyAndTenantInfos();
        }
      });
  }

  onRemoveRealm(event: Event, element: CompanyAndTenantInfo) {
    event.stopPropagation();
    const { tenantId, tenantIdentifier } = element;
    if (!tenantId || !tenantIdentifier) return;
    this.dialog
      .open(RemoveRealmDialogComponent, { data: element })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.tenantService
            .deleteTenantInfo(tenantId, tenantIdentifier)
            .subscribe(() => {
              this.reloadCompanyAndTenantInfos();
            });
        }
      });
  }
}
